import { useState } from 'react';
import { 
  X, 
  FileText, 
  Layers, 
  Target, 
  BarChart, 
  PlayCircle,
  Users,
  HelpCircle,
  Lightbulb,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  TrendingUp,
  Shield,
  Clock,
  DollarSign,
  Zap,
  Eye,
  MessageSquare,
  AlertCircle,
  Star,
  Building2,
  Rocket
} from 'lucide-react';
import GenerationProgress from './GenerationProgress';
import { DocumentContent } from './DocumentViewer';
import { generateManagedDocumentWithAI } from '../services/openaiService';
import { QUALITY_LEVELS, type QualityLevel } from '../data/gestionDocumentaireTypes';

interface PlanDirecteurModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'explainer' | 'prompts';
}

export default function PlanDirecteurModal({
  isOpen,
  onClose,
  initialTab = 'explainer',
}: PlanDirecteurModalProps) {
  const [activeTab, setActiveTab] = useState<'esprit' | 'qui' | 'quand' | 'valeur' | 'niveaux' | 'simulation'>(initialTab === 'explainer' ? 'esprit' : initialTab === 'prompts' ? 'niveaux' : 'simulation');
  const [isGeneratingExample, setIsGeneratingExample] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  const [exampleContent, setExampleContent] = useState<string | null>(null);
  const [simulationLevel, setSimulationLevel] = useState<QualityLevel>(3);

  if (!isOpen) return null;

  const handleGenerateExample = async () => {
    setIsGeneratingExample(true);
    setExampleContent(null);
    setGenerationProgress(0);
    setGenerationStep('');

    try {
      const levelDef = QUALITY_LEVELS.find((l) => l.level === simulationLevel);

      const steps = [
        { progress: 10, text: 'Analyse de la prise de besoin mock...', delay: 1000 },
        { progress: 25, text: 'Construction de la structure Plan Directeur (AS-IS / TO-BE)...', delay: 1600 },
        { progress: 45, text: 'Intégration des axes stratégiques (sécurité, cloud, data, équipe)...', delay: 2000 },
        { progress: 70, text: 'Élaboration de la roadmap 3 ans (macro)...', delay: 2200 },
        { progress: 88, text: 'Finalisation, cohérence globale et ton exécutif...', delay: 2000 },
      ];

      let stepIndex = 0;
      const interval = setInterval(() => {
        if (stepIndex < steps.length) {
          const step = steps[stepIndex];
          setGenerationStep(step.text);
          setGenerationProgress(step.progress);
          stepIndex++;
        }
      }, 2000);

      const mockContext = `Client: PME manufacturière de 80 employés, croissance 10%/an, 2 sites au Québec.
Objectifs d'affaires: améliorer la productivité, réduire les arrêts de production, mieux exploiter les données.
Maturité TI: équipe TI très limitée, beaucoup de dépendance à des fournisseurs externes, parc hétérogène, peu de standardisation.
Enjeux TI: vieillissement des serveurs, sauvegardes fragiles, cybersécurité minimale, irritants utilisateurs (lenteur, instabilité).
Opportunités: migration progressive vers le cloud, modernisation des applications critiques, meilleure gouvernance TI.`;

      const result = await generateManagedDocumentWithAI({
        documentTypeCode: 'PLAN_DIRECTEUR',
        documentTypeTitle: 'Plan Directeur TI (3-5 ans)',
        qualityLevel: simulationLevel,
        qualityLabel: levelDef?.label || `Niveau ${simulationLevel}`,
        frameworks: ['ITIL', 'COBIT', 'NIST CSF'],
        context: mockContext,
      });

      clearInterval(interval);
      setGenerationProgress(100);
      setGenerationStep('Génération terminée !');
      await new Promise((resolve) => setTimeout(resolve, 800));

      setExampleContent(result.content);
      setIsGeneratingExample(false);
    } catch (error) {
      console.error('Erreur simulation Plan Directeur:', error);
      alert(
        '❌ Erreur lors de la simulation du Plan Directeur.\n\nVérifiez que la clé OpenAI est configurée côté serveur (OPENAI_API_KEY) et regardez la console (F12) pour plus de détails.'
      );
      setIsGeneratingExample(false);
      setGenerationProgress(0);
      setGenerationStep('');
    }
  };

  if (isGeneratingExample && !exampleContent) {
    return (
      <GenerationProgress
        currentStep={generationStep || 'Préparation du Plan Directeur mock...'}
        progress={generationProgress}
        documentLabels={{
          doc1: `Plan Directeur TI – Exemple (Niveau ${simulationLevel})`,
          doc2: 'Roadmap 3 ans (macro)',
        }}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-[90vw] h-[90vh] max-w-[1600px] rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header amélioré */}
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center shadow-lg backdrop-blur-sm">
              <FileText className="w-7 h-7" />
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-1">Plan Directeur TI – Guide Complet</h2>
              <p className="text-sm text-white/90">
                Document stratégique 3-5 ans • Vision, alignement affaires/TI, feuille de route et gouvernance
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs améliorés */}
        <div className="flex gap-2 border-b-2 border-gray-200 px-8 pt-4 pb-3 bg-gradient-to-b from-gray-50 to-white overflow-x-auto">
          <button
            onClick={() => setActiveTab('esprit')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'esprit'
                ? 'bg-indigo-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <Lightbulb className="w-4 h-4" />
            Esprit & Pourquoi
          </button>
          <button
            onClick={() => setActiveTab('qui')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'qui'
                ? 'bg-indigo-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <Users className="w-4 h-4" />
            À qui livrer?
          </button>
          <button
            onClick={() => setActiveTab('quand')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'quand'
                ? 'bg-indigo-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <HelpCircle className="w-4 h-4" />
            Quand & Pourquoi?
          </button>
          <button
            onClick={() => setActiveTab('valeur')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'valeur'
                ? 'bg-indigo-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Valeur Client
          </button>
          <button
            onClick={() => setActiveTab('niveaux')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'niveaux'
                ? 'bg-indigo-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <Layers className="w-4 h-4" />
            Les 5 Niveaux
          </button>
          <button
            onClick={() => setActiveTab('simulation')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
              activeTab === 'simulation'
                ? 'bg-indigo-600 text-white shadow-lg scale-105'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            <PlayCircle className="w-4 h-4" />
            Simulation
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-gray-50">
          {/* ONGLET 1: ESPRIT & POURQUOI */}
          {activeTab === 'esprit' && (
            <div className="max-w-5xl mx-auto space-y-6">
              {/* Hero Section */}
              <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-8 text-white shadow-xl">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Lightbulb className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">L'Esprit du Plan Directeur TI</h3>
                    <p className="text-lg text-white/90 leading-relaxed">
                      Le Plan Directeur TI n'est pas juste un document technique. C'est la <strong>vision stratégique</strong> qui transforme 
                      la technologie en <strong>avantage concurrentiel</strong> pour votre client. C'est le pont entre les objectifs d'affaires 
                      et les capacités TI nécessaires pour les atteindre.
                    </p>
                  </div>
                </div>
              </div>

              {/* Pourquoi existe-t-il? */}
              <div className="bg-white rounded-xl border-2 border-indigo-100 p-6 shadow-lg">
                <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4">
                  <Target className="w-6 h-6 text-indigo-600" />
                  Pourquoi ce document existe-t-il?
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-indigo-600" />
                      <h5 className="font-semibold text-gray-900">Le Problème</h5>
                    </div>
                    <p className="text-sm text-gray-700">
                      Sans vision stratégique, les investissements TI sont <strong>décousus</strong>. On achète des outils qui ne parlent pas ensemble, 
                      on investit dans des technologies qui seront obsolètes, on ne construit pas de <strong>fondations solides</strong>.
                    </p>
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      <h5 className="font-semibold text-gray-900">La Solution</h5>
                    </div>
                    <p className="text-sm text-gray-700">
                      Le Plan Directeur établit une <strong>vision cohérente</strong> sur 3-5 ans, aligne les investissements avec les objectifs d'affaires, 
                      et crée une <strong>feuille de route</strong> claire pour transformer la TI en levier de croissance.
                    </p>
                  </div>
                </div>
              </div>

              {/* Analogie */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
                <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-3">
                  <BookOpen className="w-5 h-5 text-purple-600" />
                  Une Analogie Simple
                </h4>
                <div className="bg-purple-50 rounded-lg p-5 border-l-4 border-purple-500">
                  <p className="text-gray-800 leading-relaxed">
                    Imaginez que vous construisez une <strong>maison</strong>. Vous ne commencez pas par acheter des matériaux au hasard. 
                    Vous avez d'abord un <strong>plan d'architecte</strong> qui montre:
                  </p>
                  <ul className="list-disc list-inside mt-3 space-y-2 text-gray-700">
                    <li>L'état actuel du terrain (AS-IS)</li>
                    <li>La maison que vous voulez (TO-BE / Vision)</li>
                    <li>Les étapes de construction (Roadmap)</li>
                    <li>Le budget et les ressources nécessaires</li>
                    <li>Les risques et comment les gérer</li>
                  </ul>
                  <p className="mt-3 text-gray-800">
                    Le Plan Directeur TI, c'est exactement ça: le <strong>plan d'architecte</strong> de l'infrastructure TI de votre client 
                    pour les 3-5 prochaines années.
                  </p>
                </div>
              </div>

              {/* Les 5 Piliers */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
                <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-4">
                  <Layers className="w-5 h-5 text-indigo-600" />
                  Les 5 Piliers Fondamentaux
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { icon: Eye, title: '1. Vision & Alignement', desc: 'Où veut aller l\'entreprise? Comment la TI peut l\'y amener?', color: 'indigo' },
                    { icon: BarChart, title: '2. État des Lieux (AS-IS)', desc: 'Forces, faiblesses, dette technologique, risques actuels', color: 'blue' },
                    { icon: Target, title: '3. Vision Cible (TO-BE)', desc: 'L\'état TI idéal dans 3-5 ans, aligné sur les objectifs d\'affaires', color: 'purple' },
                    { icon: Rocket, title: '4. Roadmap Stratégique', desc: 'Les grands chantiers, priorités, séquence et dépendances', color: 'emerald' },
                    { icon: Shield, title: '5. Gouvernance & Pilotage', desc: 'Qui pilote, quels KPIs, quels rituels de suivi', color: 'orange' },
                  ].map((pillar, idx) => (
                    <div key={idx} className={`bg-${pillar.color}-50 rounded-lg p-4 border border-${pillar.color}-200`}>
                      <div className="flex items-center gap-2 mb-2">
                        <pillar.icon className={`w-5 h-5 text-${pillar.color}-600`} />
                        <h5 className="font-semibold text-gray-900 text-sm">{pillar.title}</h5>
                      </div>
                      <p className="text-xs text-gray-700">{pillar.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pourquoi PRIsME */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200 p-6 shadow-md">
                <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-3">
                  <Star className="w-5 h-5 text-yellow-500" />
                  Pourquoi PRIsME excelle dans ce livrable
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">Prise de besoin holistique</p>
                      <p className="text-sm text-gray-700">Notre questionnaire couvre TOUS les aspects: affaires, TI, sécurité, conformité, métier spécifique</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">IA + Expertise humaine</p>
                      <p className="text-sm text-gray-700">L'IA génère une base solide, l'expert BZ valide, enrichit et personnalise</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">5 niveaux de qualité</p>
                      <p className="text-sm text-gray-700">Du résumé exécutif (Niveau 1) au document CA (Niveau 5), adapté à chaque besoin</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">Standards ITIL/COBIT/NIST</p>
                      <p className="text-sm text-gray-700">Document aligné sur les meilleures pratiques internationales, crédible pour la direction</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ONGLET 2: À QUI LIVRER */}
          {activeTab === 'qui' && (
            <div className="max-w-5xl mx-auto space-y-6">
              <div className="bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-8 text-white shadow-xl">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Users className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">À qui livrer ce document?</h3>
                    <p className="text-lg text-white/90 leading-relaxed">
                      Le Plan Directeur TI s'adresse à différents profils de clients selon leur <strong>maturité TI</strong>, 
                      leur <strong>taille</strong> et leurs <strong>objectifs stratégiques</strong>. Voici les profils idéaux.
                    </p>
                  </div>
                </div>
              </div>

              {/* Profils Clients */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  {
                    title: 'PME en Croissance (20-100 employés)',
                    icon: Building2,
                    color: 'emerald',
                    scenarios: [
                      'Expansion rapide, besoin de structurer la TI',
                      'Première embauche d\'un DTI ou responsable TI',
                      'Dépendance excessive à des fournisseurs externes',
                      'Investissements TI décousus, manque de vision'
                    ],
                    niveau: 'Niveau 2-3 recommandé'
                  },
                  {
                    title: 'Entreprises Matures (100-500 employés)',
                    icon: Target,
                    color: 'indigo',
                    scenarios: [
                      'Gouvernance TI à formaliser',
                      'Alignement stratégique TI/Affaires nécessaire',
                      'Préparation à une transformation digitale',
                      'Besoin de justifier les investissements TI au CA'
                    ],
                    niveau: 'Niveau 3-4 recommandé'
                  },
                  {
                    title: 'Grandes Entreprises (500+ employés)',
                    icon: Star,
                    color: 'purple',
                    scenarios: [
                      'Document exécutif pour le CA',
                      'Refonte stratégique TI majeure',
                      'Fusion/acquisition nécessitant alignement TI',
                      'Benchmark et comparaison avec pairs'
                    ],
                    niveau: 'Niveau 4-5 recommandé'
                  },
                  {
                    title: 'Organisations Réglementées',
                    icon: Shield,
                    color: 'orange',
                    scenarios: [
                      'Conformité Loi 25, ISO 27001, NIST',
                      'Secteurs: santé, finance, assurance',
                      'Audit TI régulier requis',
                      'Gestion des risques TI critique'
                    ],
                    niveau: 'Niveau 4-5 recommandé'
                  }
                ].map((profile, idx) => (
                  <div key={idx} className={`bg-white rounded-xl border-2 border-${profile.color}-200 p-6 shadow-lg`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-lg bg-${profile.color}-100 flex items-center justify-center`}>
                        <profile.icon className={`w-6 h-6 text-${profile.color}-600`} />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold text-gray-900">{profile.title}</h4>
                        <p className={`text-xs font-semibold text-${profile.color}-600`}>{profile.niveau}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-700 mb-2">Cas d'usage typiques:</p>
                      {profile.scenarios.map((scenario, sIdx) => (
                        <div key={sIdx} className="flex items-start gap-2">
                          <CheckCircle2 className={`w-4 h-4 text-${profile.color}-600 mt-0.5 flex-shrink-0`} />
                          <p className="text-sm text-gray-700">{scenario}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Personas */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
                <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                  Personas: Qui lit ce document?
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { role: 'Direction Générale / CA', focus: 'Vision stratégique, ROI, risques', icon: Star },
                    { role: 'DTI / Responsable TI', focus: 'Feuille de route, budget, gouvernance', icon: Zap },
                    { role: 'Direction Financière', focus: 'Budget, investissements, justification', icon: DollarSign },
                  ].map((persona, idx) => (
                    <div key={idx} className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <persona.icon className="w-5 h-5 text-blue-600" />
                        <h5 className="font-semibold text-gray-900 text-sm">{persona.role}</h5>
                      </div>
                      <p className="text-xs text-gray-700">{persona.focus}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ONGLET 3: QUAND & POURQUOI */}
          {activeTab === 'quand' && (
            <div className="max-w-5xl mx-auto space-y-6">
              <div className="bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl p-8 text-white shadow-xl">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <HelpCircle className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">Quand et pourquoi le client en a besoin?</h3>
                    <p className="text-lg text-white/90 leading-relaxed">
                      Identifier les <strong>signaux</strong> qui indiquent qu'un Plan Directeur TI est nécessaire. 
                      Posez ces questions pour déterminer si votre client en a besoin.
                    </p>
                  </div>
                </div>
              </div>

              {/* Questions à Poser */}
              <div className="bg-white rounded-xl border-2 border-purple-100 p-6 shadow-lg">
                <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4">
                  <MessageSquare className="w-6 h-6 text-purple-600" />
                  Questions à poser au client
                </h4>
                <div className="space-y-4">
                  {[
                    {
                      question: 'Avez-vous une vision claire de l\'évolution de votre TI sur les 3-5 prochaines années?',
                      siNon: '→ Besoin d\'un Plan Directeur pour établir cette vision'
                    },
                    {
                      question: 'Vos investissements TI sont-ils alignés avec vos objectifs d\'affaires?',
                      siNon: '→ Le Plan Directeur aligne TI et stratégie d\'affaires'
                    },
                    {
                      question: 'Avez-vous des irritants TI récurrents (lenteur, pannes, sécurité)?',
                      siNon: '→ Le Plan Directeur identifie les causes racines et propose des solutions'
                    },
                    {
                      question: 'Votre direction comprend-elle la valeur de la TI pour l\'entreprise?',
                      siNon: '→ Le Plan Directeur démontre la valeur stratégique de la TI'
                    },
                    {
                      question: 'Avez-vous besoin de justifier un budget TI important?',
                      siNon: '→ Le Plan Directeur justifie les investissements avec une vision claire'
                    },
                    {
                      question: 'Votre entreprise est-elle en croissance ou transformation?',
                      siNon: '→ Le Plan Directeur guide la transformation TI nécessaire'
                    }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-500">
                      <p className="font-semibold text-gray-900 mb-1">{item.question}</p>
                      <p className="text-sm text-gray-700 flex items-center gap-2">
                        <ArrowRight className="w-4 h-4 text-purple-600" />
                        {item.siNon}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Signaux d'Alarme */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
                <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-4">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  Signaux d'alarme: Le client a BESOIN d'un Plan Directeur si...
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Investissements TI décousus, sans vision globale',
                    'Équipe TI réactive, pas proactive',
                    'Dette technologique importante',
                    'Risques de sécurité non gérés',
                    'Manque de gouvernance TI formelle',
                    'Direction ne comprend pas la valeur TI',
                    'Croissance rapide sans plan TI',
                    'Conformité réglementaire à gérer'
                  ].map((signal, idx) => (
                    <div key={idx} className="flex items-start gap-2 bg-orange-50 rounded-lg p-3 border border-orange-200">
                      <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-gray-800">{signal}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timing */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200 p-6 shadow-md">
                <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-3">
                  <Clock className="w-5 h-5 text-indigo-600" />
                  Timing idéal pour livrer un Plan Directeur
                </h4>
                <div className="space-y-3">
                  {[
                    { timing: 'Début d\'année fiscale', raison: 'Pour aligner le budget TI avec la stratégie' },
                    { timing: 'Avant un projet majeur', raison: 'Pour s\'assurer que le projet s\'inscrit dans la vision' },
                    { timing: 'Lors d\'une transformation', raison: 'Pour guider la transformation TI nécessaire' },
                    { timing: 'Avant un audit TI', raison: 'Pour démontrer la maturité et la gouvernance' },
                    { timing: 'Lors d\'une fusion/acquisition', raison: 'Pour aligner les infrastructures TI' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-start gap-3 bg-white rounded-lg p-3">
                      <Clock className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-gray-900">{item.timing}</p>
                        <p className="text-sm text-gray-700">{item.raison}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ONGLET 4: VALEUR CLIENT */}
          {activeTab === 'valeur' && (
            <div className="max-w-5xl mx-auto space-y-6">
              <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-8 text-white shadow-xl">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <TrendingUp className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">Comment expliquer la valeur au client?</h3>
                    <p className="text-lg text-white/90 leading-relaxed">
                      Le Plan Directeur TI n'est pas un coût, c'est un <strong>investissement stratégique</strong>. 
                      Voici comment présenter sa valeur de manière convaincante.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bénéfices Concrets */}
              <div className="bg-white rounded-xl border-2 border-emerald-100 p-6 shadow-lg">
                <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                  Bénéfices concrets pour le client
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    {
                      benefice: 'Vision claire 3-5 ans',
                      detail: 'Savoir où investir et pourquoi, éviter les décisions à court terme coûteuses',
                      icon: Eye,
                      color: 'indigo'
                    },
                    {
                      benefice: 'Alignement TI/Affaires',
                      detail: 'Chaque investissement TI sert un objectif d\'affaires concret et mesurable',
                      icon: Target,
                      color: 'blue'
                    },
                    {
                      benefice: 'Réduction des risques',
                      detail: 'Identifier et mitiger les risques TI avant qu\'ils ne deviennent des crises',
                      icon: Shield,
                      color: 'orange'
                    },
                    {
                      benefice: 'Optimisation du budget',
                      detail: 'Éviter les investissements redondants, prioriser ce qui apporte le plus de valeur',
                      icon: DollarSign,
                      color: 'emerald'
                    },
                    {
                      benefice: 'Gouvernance structurée',
                      detail: 'Décisions TI basées sur des données, pas sur des intuitions ou pressions',
                      icon: BarChart,
                      color: 'purple'
                    },
                    {
                      benefice: 'Avantage concurrentiel',
                      detail: 'TI comme levier de croissance et différenciation, pas juste un centre de coût',
                      icon: Rocket,
                      color: 'pink'
                    }
                  ].map((benefice, idx) => (
                    <div key={idx} className={`bg-${benefice.color}-50 rounded-lg p-4 border border-${benefice.color}-200`}>
                      <div className="flex items-center gap-2 mb-2">
                        <benefice.icon className={`w-5 h-5 text-${benefice.color}-600`} />
                        <h5 className="font-semibold text-gray-900">{benefice.benefice}</h5>
                      </div>
                      <p className="text-sm text-gray-700">{benefice.detail}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* ROI */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-md">
                <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-4">
                  <DollarSign className="w-5 h-5 text-emerald-600" />
                  Retour sur investissement (ROI)
                </h4>
                <div className="bg-emerald-50 rounded-lg p-5 border-l-4 border-emerald-500">
                  <p className="text-gray-800 leading-relaxed mb-3">
                    <strong>Un Plan Directeur bien fait peut générer un ROI de 300-500%</strong> en évitant:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li><strong>Investissements redondants:</strong> Éviter d'acheter 3 outils qui font la même chose = économie de 50-100K$/an</li>
                    <li><strong>Dette technologique:</strong> Planifier les mises à jour évite les crises coûteuses = économie de 100-200K$</li>
                    <li><strong>Risques de sécurité:</strong> Prévenir une cyberattaque = économie potentielle de 500K$+</li>
                    <li><strong>Décisions réactives:</strong> Agir proactivement vs réactif = gain d'efficacité de 20-30%</li>
                  </ul>
                  <p className="mt-3 text-gray-800">
                    <strong>Coût d'un Plan Directeur PRIsME:</strong> 5-15K$ (selon niveau)<br />
                    <strong>Valeur générée:</strong> 50-200K$+ en économies et opportunités
                  </p>
                </div>
              </div>

              {/* Arguments de Vente */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200 p-6 shadow-md">
                <h4 className="flex items-center gap-3 text-lg font-bold text-gray-900 mb-3">
                  <MessageSquare className="w-5 h-5 text-indigo-600" />
                  Arguments de vente par persona
                </h4>
                <div className="space-y-4">
                  {[
                    {
                      persona: 'Pour la Direction Générale',
                      arguments: [
                        'Document exécutif qui justifie les investissements TI',
                        'Vision stratégique alignée sur les objectifs d\'affaires',
                        'Réduction des risques TI (cybersécurité, conformité)',
                        'Avantage concurrentiel grâce à une TI moderne'
                      ]
                    },
                    {
                      persona: 'Pour le DTI / Responsable TI',
                      arguments: [
                        'Feuille de route claire pour guider les décisions',
                        'Budget justifié et priorisé selon la stratégie',
                        'Gouvernance TI structurée et crédible',
                        'Outil de communication avec la direction'
                      ]
                    },
                    {
                      persona: 'Pour la Direction Financière',
                      arguments: [
                        'Planification budgétaire 3-5 ans',
                        'Justification ROI de chaque investissement TI',
                        'Réduction des coûts à long terme',
                        'Gestion des risques financiers liés à la TI'
                      ]
                    }
                  ].map((item, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="font-bold text-gray-900 mb-2">{item.persona}</h5>
                      <ul className="space-y-1">
                        {item.arguments.map((arg, aIdx) => (
                          <li key={aIdx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                            {arg}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ONGLET 5: LES 5 NIVEAUX */}
          {activeTab === 'niveaux' && (
            <div className="max-w-6xl mx-auto space-y-6">
              <div className="bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl p-8 text-white shadow-xl">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-sm">
                    <Layers className="w-8 h-8" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">Les 5 Niveaux de Qualité</h3>
                    <p className="text-lg text-white/90 leading-relaxed">
                      PRIsME offre <strong>5 niveaux</strong> de Plan Directeur, du résumé exécutif au document CA. 
                      Chaque niveau s'adapte à un besoin spécifique et un budget différent.
                    </p>
                  </div>
                </div>
              </div>

              {/* Comparaison Visuelle */}
              <div className="bg-white rounded-xl border-2 border-orange-100 p-6 shadow-lg">
                <h4 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4">
                  <BarChart className="w-6 h-6 text-orange-600" />
                  Comparaison des 5 niveaux
                </h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-200 px-4 py-3 text-left text-sm font-bold text-gray-900">Critère</th>
                        {QUALITY_LEVELS.map((level) => (
                          <th key={level.level} className="border border-gray-200 px-4 py-3 text-center text-sm font-bold text-gray-900">
                            Niveau {level.level}<br />
                            <span className="text-xs font-normal text-gray-600">{level.shortLabel}</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { critere: 'Pages (approximatif)', values: ['2-4', '6-10', '15-25', '30-45', '50-70'] },
                        { critere: 'Public cible', values: ['Direction (résumé)', 'Direction + DTI', 'Direction + Comité TI', 'Comité Direction', 'CA / Conseil'] },
                        { critere: 'Détail technique', values: ['Minimal', 'Léger', 'Moyen', 'Élevé', 'Très élevé'] },
                        { critere: 'Roadmap', values: ['Lignes directrices', 'Macro 12-24 mois', 'Détaillée 3 ans', 'Très détaillée 3-5 ans', 'Complète avec scénarios'] },
                        { critere: 'Budget', values: ['Estimations globales', 'Par pilier', 'Détaillé par pilier', 'Très détaillé + CAPEX/OPEX', 'Scénarios + ROI'] },
                        { critere: 'Gouvernance', values: ['Mentionnée', 'Basique', 'Structurée', 'Complète (RACI, KPIs)', 'Exécutive (CA-ready)'] },
                        { critere: 'Risques', values: ['Liste simple', 'Analyse basique', 'Analyse + mitigation', 'Analyse complète + plan', 'Analyse stratégique + scénarios'] },
                        { critere: 'Prix indicatif', values: ['5-8K$', '8-12K$', '12-18K$', '18-25K$', '25-35K$'] },
                      ].map((row, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-900">{row.critere}</td>
                          {row.values.map((value, vIdx) => (
                            <td key={vIdx} className="border border-gray-200 px-4 py-3 text-sm text-center text-gray-700">{value}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Détails par Niveau */}
              <div className="space-y-4">
                {QUALITY_LEVELS.map((level, idx) => (
                  <div key={idx} className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-md">
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-16 h-16 rounded-xl bg-gradient-to-br from-${['indigo', 'blue', 'purple', 'orange', 'red'][idx]}-500 to-${['purple', 'cyan', 'pink', 'amber', 'rose'][idx]}-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg`}>
                        {level.level}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-gray-900">{level.label}</h4>
                        <p className="text-sm text-gray-600">{level.shortLabel}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          Inclus dans ce niveau:
                        </h5>
                        <ul className="space-y-1 text-sm text-gray-700">
                          {level.level === 1 && [
                            'Résumé exécutif (1-2 pages)',
                            'Contexte et enjeux majeurs',
                            '3-5 grandes orientations TI',
                            'Lignes directrices (pas de roadmap détaillée)',
                            'Langage simple, accessible'
                          ].map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <ArrowRight className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                          {level.level === 2 && [
                            'Résumé exécutif + sections standardisées',
                            'État des lieux TI (AS-IS)',
                            'Enjeux & risques identifiés',
                            'Axes stratégiques (4-5)',
                            'Roadmap macro 12-24 mois',
                            'Prochaines étapes 6-12 mois'
                          ].map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <ArrowRight className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                          {level.level === 3 && [
                            'Tout le niveau 2, PLUS:',
                            'AS-IS détaillé par pilier TI',
                            'TO-BE (vision cible) détaillée',
                            'Roadmap 3 ans avec priorités',
                            'Budget estimé par pilier',
                            'Risques + dépendances',
                            'Intégration ITIL/COBIT/NIST'
                          ].map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <ArrowRight className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                          {level.level === 4 && [
                            'Tout le niveau 3, PLUS:',
                            'Modèle de gouvernance TI (RACI)',
                            'KPIs TI clés (disponibilité, sécurité, projets)',
                            'Plan de mitigation des risques',
                            'Budget CAPEX/OPEX détaillé',
                            'Gestion de portefeuille TI',
                            'Ton très professionnel, comité-ready'
                          ].map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <ArrowRight className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                          {level.level === 5 && [
                            'Tout le niveau 4, PLUS:',
                            'Scénarios (conservateur, ambitieux)',
                            'Estimation ROI par initiative',
                            'Benchmarks sectoriels',
                            'Risques stratégiques + plan',
                            'Document CA-ready',
                            'Ton exécutif, très visuel'
                          ].map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <ArrowRight className="w-3 h-3 text-gray-400 mt-1 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                          <Target className="w-4 h-4 text-indigo-600" />
                          Quand utiliser ce niveau:
                        </h5>
                        <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                          {level.level === 1 && 'Pour un premier contact, donner une vue d\'ensemble rapide, budget limité, direction non technique.'}
                          {level.level === 2 && 'Pour PME standard, besoin de structuration, première approche formelle de la gouvernance TI.'}
                          {level.level === 3 && 'Pour PME en croissance, besoin complet, alimenter budget et projets, comité TI formel.'}
                          {level.level === 4 && 'Pour entreprises matures, gouvernance importante, pilotage actif, comité de direction.'}
                          {level.level === 5 && 'Pour grandes entreprises, présentation CA, transformation majeure, benchmark nécessaire.'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ONGLET 6: SIMULATION */}
          {activeTab === 'simulation' && (
            <div className="max-w-5xl mx-auto space-y-6">
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-lg flex items-start gap-4">
                <div className="mt-1">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                    <PlayCircle className="w-6 h-6" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Simulation d&apos;un Plan Directeur avec données mock
                  </h3>
                  <p className="text-sm text-gray-700 mb-4">
                    Cette simulation utilise le même moteur OpenAI que PRIsME utilisera pour générer un vrai
                    Plan Directeur pour un client. On injecte une prise de besoin &quot;mock&quot; (PME manufacturière)
                    et on génère un document selon le <strong>niveau de livrable</strong> choisi ci-dessous.
                  </p>

                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <span className="text-sm text-gray-700 font-semibold">Niveau de simulation:</span>
                    {QUALITY_LEVELS.map((lvl) => (
                      <button
                        key={lvl.level}
                        onClick={() => setSimulationLevel(lvl.level)}
                        className={`px-4 py-2 rounded-lg border text-sm font-semibold transition-all ${
                          simulationLevel === lvl.level
                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-lg scale-105'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        <span className="font-bold mr-1">{lvl.level}</span>
                        <span>{lvl.shortLabel}</span>
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={handleGenerateExample}
                    disabled={isGeneratingExample}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
                  >
                    <PlayCircle className="w-5 h-5" />
                    {isGeneratingExample
                      ? 'Simulation en cours...'
                      : `Lancer la simulation Plan Directeur (mock) – Niveau ${simulationLevel}`}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    Note: nécessite que la clé OpenAI soit configurée côté serveur. La génération peut prendre
                    quelques secondes.
                  </p>
                </div>
              </div>

              {exampleContent && (
                <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-lg">
                  <h4 className="text-lg font-bold text-gray-900 mb-4">
                    Exemple de document généré (mock) – Vue PRIsME
                  </h4>
                  <div className="border border-gray-200 rounded-lg max-h-[60vh] overflow-y-auto bg-gray-50 px-6 py-4">
                    <DocumentContent content={exampleContent} color="indigo" />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
